import type { IconName } from 'lucide-react/dynamic';
import { cn } from '@/lib/utils';
import { Icon } from './icon';

interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  icon?: IconName;
}

interface SegmentedProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  options: SegmentedOption<T>[];
  className?: string;
}

export function Segmented<T extends string>({
  value,
  onChange,
  options,
  className,
}: SegmentedProps<T>) {
  return (
    <div
      className={cn(
        'inline-flex items-center gap-0.5 rounded-[9px] border border-border bg-raised p-[3px]',
        className,
      )}
    >
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          title={option.label}
          aria-label={option.label}
          onClick={() => onChange(option.value)}
          className={cn(
            'flex items-center justify-center gap-1.5 rounded-[7px] px-2.5 py-1 text-[12px] font-semibold transition-colors duration-150',
            option.icon && 'h-full aspect-square px-0',
            value === option.value
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {option.icon ? <Icon name={option.icon} size={15} /> : option.label}
        </button>
      ))}
    </div>
  );
}
